/**
 * TeamLeader Exam Workspace — `/team-leader/exams/$examId`.
 *
 * The route itself is thin by design: it declares the URL search contract and hands the resolved
 * search plus a navigation function to `<ExamWorkspace/>`. Everything else — header, tabs, roster,
 * question analytics, the Student Sheet — lives in `components/team-leader/exams/`.
 *
 * The URL is unchanged and no redirect is needed: the workspace's tabs are search params, so every
 * existing link and bookmark to this route still resolves, landing on Overview.
 *
 * Route naming follows this codebase's convention for a detail page that sits alongside (not nested
 * under) its list page: `exams_.$examId.tsx` (see `team-leader/groups_.$groupId.tsx`) makes this a
 * SIBLING of `exams.tsx`, not a child requiring an <Outlet/> there.
 */
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { ExamWorkspace } from "@/components/team-leader/exams/ExamWorkspace";
import {
  validateExamWorkspaceSearch,
  type ExamWorkspaceSearch,
} from "@/components/team-leader/exams/exam-workspace-search";

export const Route = createFileRoute("/_authenticated/team-leader/exams_/$examId")({
  validateSearch: validateExamWorkspaceSearch,
  component: TeamLeaderExamWorkspaceRoute,
});

function TeamLeaderExamWorkspaceRoute() {
  const { examId } = useParams({ from: "/_authenticated/team-leader/exams_/$examId" });
  const search = Route.useSearch();
  const navigate = useNavigate();

  return (
    <ExamWorkspace
      examId={examId}
      search={search}
      navigateSearch={(next: ExamWorkspaceSearch, opts) =>
        navigate({
          to: "/team-leader/exams/$examId",
          params: { examId },
          search: next,
          replace: opts.replace,
          state: opts.state,
        })
      }
    />
  );
}
